import { useState } from "react";
import { PackagePlus, Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function RequestTool({ defaultName = "", className = "" }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const openDialog = () => { setName(defaultName || ""); setNote(""); setOpen(true); };

  const submit = async () => {
    if (name.trim().length < 2) return toast.error("Tell us the tool's name first.");
    setSending(true);
    try {
      const { data } = await api.post("/tools/request", { name: name.trim(), note: note.trim() });
      toast.success(data.message || "Request sent!");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send the request.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="request-tool-btn"
        onClick={openDialog}
        className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-border text-sm font-semibold text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors ${className}`}
      >
        <PackagePlus className="h-4 w-4" /> Request a tool
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="request-tool-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Request an AI tool 🛠️</DialogTitle>
            <DialogDescription>
              Tell us which tool you want a full course on — we curate the most-requested ones first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="request-tool-name"
              placeholder="Tool name (e.g. HeyGen, Perplexity…)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-xl"
              maxLength={80}
            />
            <Textarea
              data-testid="request-tool-note"
              placeholder="What do you want to do with it? (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-xl min-h-[80px]"
              maxLength={500}
            />
            <Button data-testid="request-tool-submit" onClick={submit} disabled={sending} className="w-full rounded-xl">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
